import React, { useMemo, useState } from "react";
import { Box, Flex, Grid, Image, Link, Skeleton, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomApprovalModal from "@/shared/components/custom-modal";
import StatusBadge from "@/shared/components/StatusBadge";
import PathPickMarginFormulaModal from "@/features/pathpick/components/product/PathPickMarginFormulaModal";
import PathPickAiImageBadge from "@/features/pathpick/components/product/PathPickAiImageBadge";
import PathPickProductImageZoomTooltip from "@/features/pathpick/components/product/PathPickProductImageZoomTooltip";
import PathPickPoizonSellerCenterModal from "@/features/pathpick/components/product/PathPickPoizonSellerCenterModal";
import PathPickProductDetailMarketCard from "@/features/pathpick/components/product/PathPickProductDetailMarketCard";
import PathPickProductOptionTable, {
  PathPickOptionMarginTarget,
} from "@/features/pathpick/components/product/PathPickProductOptionTable";
import CustomIcon from "../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  PathPickMarket,
  PathPickMarketPriceBasis,
  PathPickProduct,
  PathPickProductCardViewSettings,
} from "@/features/pathpick/types";
import {
  getProductImageUrl,
  isProductImageAiCollected,
  getProductTotalStock,
} from "@/features/pathpick/utils";
import {
  getPathPickMarketExternalUrl,
  getPathPickMarketMetrics,
  PATH_PICK_MARKETS,
  PATH_PICK_MARKET_BASIS_LABEL_KEY,
  PATH_PICK_MARKET_LABEL,
} from "@/features/pathpick/utils/market";
import {
  buildPathPickProductOptionTable,
  getPathPickMarketBannerCase,
  PathPickMarketBannerCase,
} from "@/features/pathpick/utils/productOptions";
import useGetKreamOptions from "@/features/pathpick/hooks/query/useGetKreamOptions";
import useGetPoizonOptions from "@/features/pathpick/hooks/query/useGetPoizonOptions";
import usePathPickProductCardView from "@/features/pathpick/hooks/usePathPickProductCardView";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  item: PathPickProduct;
  // 시작 가이드는 사용자의 보기 설정과 무관하게 항상 비교를 보여준다(카드와 같은 규칙).
  viewOverride?: PathPickProductCardViewSettings;
}

interface InfoFieldProps {
  label: string;
  value: string;
}

const InfoField = ({ label, value }: InfoFieldProps) => (
  <Flex flexDirection={"column"} gap={"2px"}>
    <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
      {label}
    </Text>
    <Text textStyle={"body-sm-bold"} color={"#444440"}>
      {value || "-"}
    </Text>
  </Flex>
);

interface ProductImageThumbnailProps {
  imageUrl?: string;
  alt: string;
  // SKU 로 모인 AI 수집 이미지면 배지를 달아 상품 자체 이미지와 구분한다.
  isAiCollected?: boolean;
}

// 호버 시 확대 이미지를 띄운다 (툴팁 구현은 상품 카드와 공유한다).
const ProductImageThumbnail = ({
  imageUrl,
  alt,
  isAiCollected = false,
}: ProductImageThumbnailProps) => {
  return (
    <PathPickProductImageZoomTooltip
      imageUrl={imageUrl}
      alt={alt}
      isAiCollected={isAiCollected}
    >
      {/* fallbackSrc로 엘리먼트가 교체되는 Image에 직접 ref를 걸지 않도록 Box로 감싼다. */}
      <Box position={"relative"}>
        <Image
          src={imageUrl}
          fallbackSrc={"/icons-v1_1/null.png"}
          boxSize={"120px"}
          objectFit={"contain"}
          background={"#FFFFFF"}
          border={"0.5px solid #D4D4D1"}
          borderRadius={"8px"}
          alt={alt}
        />
        {isAiCollected && <PathPickAiImageBadge />}
      </Box>
    </PathPickProductImageZoomTooltip>
  );
};

interface BannerCaseConfig {
  color: string;
  borderColor: string;
  background: string;
  titleKey: string;
  // 둘째 줄에 매칭/미매칭 개수를 보여줄지 — false면 설명 문구(descriptionKey)를 쓴다.
  showCounts: boolean;
  descriptionKey?: string;
}

// 마켓별 상태 배너 — 상품 미매칭만 붉은색이고(연결 자체가 없다는 사실), 나머지는 주의색이다.
const BANNER_CASES: Record<PathPickMarketBannerCase, BannerCaseConfig> = {
  unavailable: {
    color: "#B7791F",
    borderColor: "#F0DFAE",
    background: "#FFF9E8",
    titleKey: "path_pick:search_products.size_stock_modal.banner_unavailable",
    showCounts: false,
    descriptionKey:
      "path_pick:search_products.size_stock_modal.banner_unavailable_description",
  },
  productUnmatched: {
    color: "#EE7D7A",
    borderColor: "#EE7D7A",
    background: "#FFEEEE",
    titleKey:
      "path_pick:search_products.size_stock_modal.banner_product_unmatched",
    showCounts: false,
    descriptionKey:
      "path_pick:search_products.size_stock_modal.banner_product_unmatched_description",
  },
  allUnmatched: {
    color: "#B7791F",
    borderColor: "#F0DFAE",
    background: "#FFF9E8",
    titleKey: "path_pick:search_products.size_stock_modal.banner_all_unmatched",
    showCounts: true,
  },
  partialUnmatched: {
    color: "#B7791F",
    borderColor: "#F0DFAE",
    background: "#FFF9E8",
    titleKey:
      "path_pick:search_products.size_stock_modal.banner_partial_unmatched",
    showCounts: true,
  },
};

function PathPickSizeStockModal({
  isOpen,
  onClose,
  item,
  viewOverride,
}: Props) {
  const { t, tCurrencyString } = useI18nHelper();
  // 카드에서 비교를 감춘 사용자에게는 이 모달도 마켓 얘기를 꺼내지 않는다 —
  // 시세·마진·추천·매칭은 물론 "KREAM"/"POIZON"이라는 말 자체가 나오지 않아야 한다.
  const { isComparisonVisible: savedComparisonVisible } =
    usePathPickProductCardView();
  const isComparisonVisible = viewOverride
    ? viewOverride.showComparison
    : savedComparisonVisible;

  const formatKrw = (value?: number | null) =>
    value != null
      ? tCurrencyString(value, { type: "SYMBOL", code: "KRW" })
      : "-";

  // 비교 기준 — 모달 로컬 상태 (쿼리스트링/외부 필터와 무관, 열 때마다 초기화)
  const [basis, setBasis] = useState<PathPickMarketPriceBasis>("bidPrice");
  // 마켓별로 모달을 2개 만들지 않고, 무엇의 산식을 여는지만 상태로 둔다.
  // 옵션 표에서 열면 그 사이즈의 값이 들어오고, 마켓 카드에서 열면 상품 레벨 값이 들어온다.
  const [marginTarget, setMarginTarget] =
    useState<PathPickOptionMarginTarget | null>(null);
  // 포이즌 배너 링크는 판매자 센터로 나가므로 안내 모달을 한 번 거친다 —
  // 주소를 담아 두는 것이 곧 "열려 있다" 상태다.
  const [sellerCenterUrl, setSellerCenterUrl] = useState<string | null>(null);

  const totalStock = getProductTotalStock(item);
  // 상품 자체 이미지 뒤에 SKU 로 모인 AI 수집 이미지를 붙인다. 섞지 않고 뒤에 두어야
  // 앞쪽(원본)부터 보이고, AI 이미지는 배지로 구분된다. 둘 다 없으면 플레이스홀더 한 칸.
  // 세트에 'AI 이미지 우선 노출'이 켜져 있으면 순서를 뒤집어 AI 이미지부터 보여준다(배지는 그대로).
  const galleryImages = useMemo(() => {
    const own = (item.imageUrls ?? []).map((url) => ({
      url,
      isAiCollected: false,
    }));
    const ai = (item.skuImageUrls ?? []).map((url) => ({
      url,
      isAiCollected: true,
    }));
    const images = item.preferAiImage ? [...ai, ...own] : [...own, ...ai];
    return images.length > 0
      ? images
      : [{ url: undefined, isAiCollected: false }];
  }, [item.imageUrls, item.skuImageUrls, item.preferAiImage]);

  // 사이즈별 시세는 목록에 실려오지 않는다 — 모달을 열 때 두 마켓을 각각 조회한다.
  // 비교를 감춘 사용자에게는 쓸 곳이 없으므로 조회 자체를 걸지 않는다.
  const { kreamOptions, isKreamUnavailable, isKreamOptionsPending } =
    useGetKreamOptions(item.kream?.productId, isOpen && isComparisonVisible);
  const { poizonOptions, isPoizonUnavailable, isPoizonOptionsPending } =
    useGetPoizonOptions(item.poizon?.spuId, isOpen && isComparisonVisible);
  // 한쪽만 먼저 그리면 아직 답이 오지 않은 마켓이 "미매칭"으로 단정돼 보인다 — 둘 다 기다린다.
  const isOptionsPending = isKreamOptionsPending || isPoizonOptionsPending;

  const metricsByMarket = {
    KREAM: getPathPickMarketMetrics(item, "KREAM", basis),
    POIZON: getPathPickMarketMetrics(item, "POIZON", basis),
  };

  const optionTable = useMemo(
    () =>
      buildPathPickProductOptionTable({
        item,
        kreamOptions,
        poizonOptions,
        basis,
      }),
    [item, kreamOptions, poizonOptions, basis]
  );

  // 낼 말이 있는 마켓만 배너를 만든다. 전체 매칭이면 둘 다 없어 배너 영역 자체가 사라진다.
  // 비교를 감췄으면 조회를 걸지 않아 판정할 근거도 없다 — 아예 만들지 않는다.
  const banners = !isComparisonVisible
    ? []
    : PATH_PICK_MARKETS.flatMap((market) => {
        const bannerCase = getPathPickMarketBannerCase({
          isPending: isOptionsPending,
          isUnavailable:
            market === "KREAM" ? isKreamUnavailable : isPoizonUnavailable,
          matchCount: optionTable.matchCounts[market],
        });
        return bannerCase
          ? [{ market, bannerCase, config: BANNER_CASES[bannerCase] }]
          : [];
      });

  const productTypeLabel = t(
    item.productType === "LINE_SHEET_PRODUCT"
      ? "path_pick:search_products.size_stock_modal.product_type_line_sheet_product"
      : "path_pick:search_products.size_stock_modal.product_type_product"
  );

  const openMarketMarginFormula = (market: PathPickMarket) => {
    const metrics = metricsByMarket[market];
    setMarginTarget({
      market,
      basisValue: metrics.basisValue,
      expectedMargin: metrics.expectedMargin,
      marginRate: metrics.marginRate,
      optionLabel: "",
    });
  };

  return (
    <>
      <CustomApprovalModal
        isOpen={isOpen}
        onClose={onClose}
        minWidth={"min(1280px, 96dvw)"}
        scrollBehavior={"inside"}
        isCentered={true}
      >
        <CustomApprovalModal.Header
          onClose={onClose}
          title={
            <Flex gap={"8px"} alignItems={"center"}>
              {/* TODO: 그리드 아이콘이 아이콘셋에 없어 유사 아이콘으로 대체 */}
              <CustomIcon
                src={"package"}
                width={20}
                height={20}
                color={"#1272EF"}
              />
              <Flex flexDirection={"column"} gap={"2px"}>
                <Text textStyle={"body-md-bold"} color={"#444444"}>
                  {t("path_pick:search_products.size_stock_modal.title")}
                </Text>
                <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
                  {t(
                    isComparisonVisible
                      ? "path_pick:search_products.size_stock_modal.description"
                      : "path_pick:search_products.size_stock_modal.description_no_comparison"
                  )}
                </Text>
              </Flex>
            </Flex>
          }
        />
        <CustomApprovalModal.Body>
          <Flex flexDirection={"column"} gap={"12px"}>
            <Flex
              border={"0.5px solid #D4D4D1"}
              borderRadius={"8px"}
              padding={"12px"}
              justifyContent={"space-between"}
              alignItems={"center"}
              gap={"12px"}
            >
              <Flex gap={"12px"} alignItems={"center"} overflow={"hidden"}>
                <Box position={"relative"} flexShrink={0}>
                  <Image
                    src={getProductImageUrl(item)}
                    fallbackSrc={"/icons-v1_1/null.png"}
                    boxSize={"56px"}
                    objectFit={"contain"}
                    background={"#FFFFFF"}
                    border={"0.5px solid #8F8F8C"}
                    borderRadius={"8px"}
                    alt={item.name}
                  />
                  {isProductImageAiCollected(item) && (
                    <PathPickAiImageBadge size={"small"} />
                  )}
                </Box>
                <Flex flexDirection={"column"} gap={"2px"} overflow={"hidden"}>
                  <Text
                    textStyle={"body-md-bold"}
                    color={"#444440"}
                    noOfLines={1}
                  >
                    {`${item.brandName} · ${item.name ?? ""} ${item.sku}`}
                  </Text>
                  <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                    {`${item.sku} · ${item.color}`}
                  </Text>
                </Flex>
              </Flex>
              <StatusBadge
                colorVariant={"blue"}
                size={"medium"}
                status={t(
                  "path_pick:search_products.size_stock_modal.total_count",
                  {
                    count: totalStock,
                  }
                )}
              />
            </Flex>

            {/* 비교 기준 토글 — 모달 내부에서만 동작한다(목록 필터와 독립).
                비교를 감추면 고를 기준 자체가 없다. */}
            {isComparisonVisible && (
              // 좁은 폭에서는 설명과 토글을 한 줄에 두면 설명이 몇 글자씩 꺾여 내려간다 —
              // 설명을 위로 올리고, 토글 줄은 버튼이 남은 폭을 나눠 갖게 한다.
              <Flex
                border={"0.5px solid #D4D4D1"}
                borderRadius={"8px"}
                padding={"12px"}
                flexDirection={{ base: "column", md: "row" }}
                justifyContent={"space-between"}
                alignItems={{ base: "stretch", md: "center" }}
                gap={{ base: "8px", md: "12px" }}
              >
                <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
                  {t("path_pick:search_products.size_stock_modal.basis_note")}
                </Text>
                <Flex gap={"4px"} alignItems={"center"} flexShrink={0}>
                  <Text
                    textStyle={"body-sm-bold"}
                    color={"#444440"}
                    whiteSpace={"nowrap"}
                    marginRight={{ base: "4px", md: 0 }}
                  >
                    {t("path_pick:filter.market_basis")}
                  </Text>
                  <Box
                    flex={{ base: 1, md: "none" }}
                    width={{ base: "auto", md: "100px" }}
                  >
                    <AppButton
                      variant={"white"}
                      isActive={basis === "bidPrice"}
                      onClick={() => setBasis("bidPrice")}
                      width={"100%"}
                    >
                      <Text textStyle={"body-sm-bold"}>
                        {t("path_pick:filter.basis_bid")}
                      </Text>
                    </AppButton>
                  </Box>
                  <Box
                    flex={{ base: 1, md: "none" }}
                    width={{ base: "auto", md: "100px" }}
                  >
                    <AppButton
                      variant={"white"}
                      isActive={basis === "tradePrice"}
                      onClick={() => setBasis("tradePrice")}
                      width={"100%"}
                    >
                      <Text textStyle={"body-sm-bold"}>
                        {t("path_pick:filter.basis_trade")}
                      </Text>
                    </AppButton>
                  </Box>
                </Flex>
              </Flex>
            )}

            <Flex
              flexDirection={"column"}
              gap={"8px"}
              border={"0.5px solid #D4D4D1"}
              borderRadius={"8px"}
              padding={"12px"}
            >
              <Text textStyle={"body-sm-bold"} color={"#444440"}>
                {t("path_pick:search_products.size_stock_modal.product_image")}
              </Text>
              <Flex gap={"8px"} flexWrap={"wrap"}>
                {galleryImages.map((image, index) => (
                  <ProductImageThumbnail
                    key={`${image.url ?? "fallback"}_${index}`}
                    imageUrl={image.url}
                    alt={item.name}
                    isAiCollected={image.isAiCollected}
                  />
                ))}
              </Flex>
            </Flex>

            <Grid
              templateColumns={{ base: "1fr", md: "1fr 1fr" }}
              gap={"12px 16px"}
              border={"0.5px solid #D4D4D1"}
              borderRadius={"8px"}
              padding={"12px"}
            >
              <InfoField
                label={t("path_pick:filter.brand")}
                value={item.brandName}
              />
              <InfoField label={"SKU"} value={item.sku} />
              <InfoField
                label={t("path_pick:search_products.popular.season")}
                value={item.season}
              />
              <InfoField
                label={t("path_pick:filter.category")}
                value={item.categories.join(" / ")}
              />
              <InfoField label={t("common:gender")} value={item.gender} />
              <InfoField label={t("common:color")} value={item.color} />
              <InfoField
                label={t(
                  "path_pick:search_products.size_stock_modal.product_type"
                )}
                value={productTypeLabel}
              />
              <InfoField
                label={t("path_pick:search_products.product_list.supply_price")}
                value={formatKrw(item.salePrice.value)}
              />
            </Grid>

            {/* 마켓 요약 카드 — 미매칭 마켓도 자리를 비우지 않는다(한쪽만 붙은 사실이 보여야 한다). */}
            {isComparisonVisible && (
              <Grid
                templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                gap={"12px"}
              >
                {PATH_PICK_MARKETS.map((market) => (
                  <PathPickProductDetailMarketCard
                    key={market}
                    metrics={metricsByMarket[market]}
                    basis={basis}
                    onOpenMarginFormula={() => openMarketMarginFormula(market)}
                  />
                ))}
              </Grid>
            )}

            {/* 배너 자리 — 두 줄 배너의 실제 높이(padding 12px x 2 + lineHeight 20px x 2). */}
            {isOptionsPending && (
              <Skeleton height={"64px"} borderRadius={"8px"} />
            )}

            {banners.length > 0 && (
              <Grid
                templateColumns={
                  banners.length > 1 ? { base: "1fr", md: "1fr 1fr" } : "1fr"
                }
                gap={"12px"}
              >
                {banners.map(({ market, bannerCase, config }) => {
                  const marketLabel = PATH_PICK_MARKET_LABEL[market];
                  const externalUrl = getPathPickMarketExternalUrl(
                    item,
                    market
                  );

                  return (
                    <Flex
                      key={market}
                      padding={"12px"}
                      borderRadius={"8px"}
                      border={`0.5px solid ${config.borderColor}`}
                      background={config.background}
                      justifyContent={"space-between"}
                      alignItems={"flex-start"}
                      gap={"12px"}
                    >
                      <Flex gap={"8px"} alignItems={"flex-start"}>
                        <CustomIcon
                          src={"info-circle"}
                          width={16}
                          height={16}
                          color={config.color}
                        />
                        <Flex flexDirection={"column"} gap={"2px"}>
                          <Text textStyle={"body-sm-bold"} color={config.color}>
                            {t(config.titleKey, { market: marketLabel })}
                          </Text>
                          {config.showCounts ? (
                            <Flex gap={"8px"} flexWrap={"wrap"}>
                              <Text
                                textStyle={"caption-medium"}
                                color={"#8F8F8C"}
                              >
                                {t(
                                  "path_pick:search_products.size_stock_modal.banner_matched_count",
                                  {
                                    count:
                                      optionTable.matchCounts[market].matched,
                                  }
                                )}
                              </Text>
                              <Text
                                textStyle={"caption-medium"}
                                color={"#8F8F8C"}
                              >
                                {t(
                                  "path_pick:search_products.size_stock_modal.banner_unmatched_count",
                                  {
                                    count:
                                      optionTable.matchCounts[market].unmatched,
                                  }
                                )}
                              </Text>
                            </Flex>
                          ) : (
                            config.descriptionKey && (
                              <Text
                                textStyle={"caption-medium"}
                                color={config.color}
                              >
                                {t(config.descriptionKey, {
                                  market: marketLabel,
                                })}
                              </Text>
                            )
                          )}
                        </Flex>
                      </Flex>
                      {bannerCase === "unavailable" && externalUrl && (
                        <Link
                          // 포이즌은 판매자 로그인이 필요한 셀러센터라 바로 열지 않고
                          // 안내 모달을 먼저 띄운다(크림은 공개 상품 페이지라 그대로).
                          {...(market === "POIZON"
                            ? {
                                as: "button" as const,
                                onClick: () => setSellerCenterUrl(externalUrl),
                              }
                            : { href: externalUrl, isExternal: true })}
                          flexShrink={0}
                          textStyle={"body-sm-bold"}
                          color={config.color}
                          textDecoration={"underline"}
                        >
                          {t(
                            "path_pick:search_products.size_stock_modal.banner_unavailable_link",
                            { market: marketLabel }
                          )}
                        </Link>
                      )}
                    </Flex>
                  );
                })}
              </Grid>
            )}

            <PathPickProductOptionTable
              model={optionTable}
              supplyPrice={item.salePrice.value}
              isComparisonVisible={isComparisonVisible}
              isPending={isOptionsPending}
              pendingRowCount={item.options.length}
              onOpenMarginFormula={setMarginTarget}
            />

            <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
              {t("path_pick:search_products.size_stock_modal.footer_note")}
            </Text>
          </Flex>
        </CustomApprovalModal.Body>
      </CustomApprovalModal>
      {isComparisonVisible && marginTarget && (
        <PathPickMarginFormulaModal
          isOpen
          onClose={() => setMarginTarget(null)}
          market={marginTarget.market}
          item={item}
          supplyPrice={item.salePrice.value}
          basisValue={marginTarget.basisValue}
          basisLabel={t(
            PATH_PICK_MARKET_BASIS_LABEL_KEY[marginTarget.market][basis]
          )}
          expectedMargin={marginTarget.expectedMargin}
          marginRate={marginTarget.marginRate}
          optionLabel={marginTarget.optionLabel || undefined}
        />
      )}
      {isComparisonVisible && (
        <PathPickPoizonSellerCenterModal
          isOpen={sellerCenterUrl != null}
          onClose={() => setSellerCenterUrl(null)}
          url={sellerCenterUrl}
        />
      )}
    </>
  );
}

export default PathPickSizeStockModal;
